import { useState } from "react";
import { Button } from "./ui/button";
import { CreditCard, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// PayPal SVG Icon
const PayPalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .76-.654h6.234c2.074 0 3.702.494 4.845 1.47 1.143.976 1.714 2.41 1.714 4.301 0 2.465-.862 4.393-2.585 5.785-1.724 1.392-4.084 2.088-7.08 2.088H6.884l-.808 4.627z" />
    <path d="M19.95 8.305c0 1.62-.557 2.897-1.671 3.832-1.114.935-2.632 1.402-4.553 1.402h-2.296l-.65 3.711h-2.858l1.977-11.333h5.424c1.393 0 2.477.322 3.251.966.774.645 1.376 1.5 1.376 1.422z" fill="#169BD7" />
  </svg>
);

export default function PaymentButtons({ bookingId, basePrice, onPaymentSuccess, onAlreadyPaid }) {
  const { token } = useAuth();
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [loadingPaypal, setLoadingPaypal] = useState(false);
  const [error, setError] = useState(null);

  // Desglose de comisión Plazafy (10% sobre el base)
  const base = Number(basePrice || 0);
  const fee = +(base * 0.10).toFixed(2);
  const totalPrice = +(base + fee).toFixed(2);

  // Helper to detect "already paid" backend error and trigger refetch
  const isAlreadyPaidError = (msg) =>
    typeof msg === "string" && msg.toLowerCase().includes("ya está pagada");

  const handleStripePayment = async () => {
    setLoadingStripe(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/payments/stripe/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_id: bookingId,
          payment_method: "stripe",
          origin_url: window.location.origin
        })
      });

      const data = await res.json();

      if (!res.ok) {
        if (isAlreadyPaidError(data.detail)) {
          // El backend dice que ya está pagada → sincronizar UI
          onAlreadyPaid?.();
          return;
        }
        throw new Error(data.detail || "Error al crear el pago");
      }

      // Redirigir a Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message);
      setLoadingStripe(false);
    }
  };

  const handlePaypalPayment = async () => {
    setLoadingPaypal(true);
    setError(null);

    try {
      
      // Crear la orden en nuestro backend
      const res = await fetch(`${API}/api/payments/paypal/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_id: bookingId,
          payment_method: "paypal",
          origin_url: window.location.origin
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Error al crear el pago");
      }

      // Simulación de pago PayPal (modo demo)
      const confirmed = window.confirm(
        `¿Deseas continuar con el pago de ${totalPrice}€ mediante PayPal?\n\n` +
        `(Modo demo - En producción se abriría el checkout de PayPal)`
      );

      if (confirmed) {
        // Capturar el pago
        const captureRes = await fetch(
          `${API}/api/payments/paypal/capture?booking_id=${bookingId}&paypal_order_id=SIMULATED_${Date.now()}`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const captureData = await captureRes.json();

        if (captureRes.ok) {
          onPaymentSuccess?.();
        } else {
          throw new Error(captureData.detail || "Error al capturar el pago");
        }
      }
    } catch (err) {
      console.error("PayPal error:", err);
      setError(err.message);
    } finally {
      setLoadingPaypal(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-buttons">
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="bg-gradient-to-br from-violet-500/15 to-purple-500/10 rounded-xl p-4 border border-violet-500/25 space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-300">Plaza</span>
          <span className="text-white font-medium">€{base.toFixed(2)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400 flex items-center gap-1.5">
            Tasa de servicio Plazafy
            <span className="text-[10px] text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">10%</span>
          </span>
          <span className="text-gray-300 font-medium">€{fee.toFixed(2)}</span>
        </div>
        <div className="border-t border-white/10 pt-2 flex items-end justify-between">
          <div>
            <p className="text-gray-300 text-sm">Total a pagar</p>
            <p className="text-xs text-gray-500">Incluye todos los cargos</p>
          </div>
          <p className="text-2xl font-bold text-white">€{totalPrice.toFixed(2)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {/* Stripe Button */}
        <Button
          onClick={handleStripePayment}
          disabled={loadingStripe || loadingPaypal}
          className="w-full h-14 bg-[#635BFF] hover:bg-[#5851ea] text-white font-medium text-base flex items-center justify-center gap-3"
          data-testid="stripe-pay-btn"
        >
          {loadingStripe ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Pagar con Tarjeta
            </>
          )}
        </Button>

        {/* PayPal Button */}
        <Button
          onClick={handlePaypalPayment}
          disabled={loadingStripe || loadingPaypal}
          className="w-full h-14 bg-[#FFC439] hover:bg-[#f5bb36] text-[#003087] font-bold text-base flex items-center justify-center gap-3"
          data-testid="paypal-pay-btn"
        >
          {loadingPaypal ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <PayPalIcon />
              PayPal
            </>
          )}
        </Button>
      </div>

      <p className="text-center text-gray-500 text-xs mt-4">
        Pago seguro y encriptado. Tus datos están protegidos.
      </p>
    </div>
  );
}
